import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { Button } from '../components/UI';
import SharedHeader from '../components/SharedHeader';
import SharedFooter from '../components/SharedFooter';

const AIPowered = () => {
  const { language, changeLanguage } = useLanguage();
  const t = (key) => getTranslation(language, key);

  useEffect(() => {
    // SEO Meta Tags
    const titles = {
      es: 'Cómo LIBERADO Usa IA — Inteligencia de Fuerza Laboral Veterinaria',
      en: 'How Liberado Uses AI — Veterinary Workforce Intelligence',
      pt: 'Como a LIBERADO Usa IA — Inteligência de Força de Trabalho Veterinária'
    };

    const descriptions = {
      es: 'Explore los futuros sistemas de verificación, emparejamiento y programación impulsados por IA de LIBERADO para el ecosistema veterinario.',
      en: 'Explore Liberado\'s future AI-driven verification, matching, and scheduling systems for the veterinary ecosystem.',
      pt: 'Explore os futuros sistemas de verificação, correspondência e agendamento impulsionados por IA da LIBERADO para o ecossistema veterinário.'
    };

    document.title = titles[language] || titles.es;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = descriptions[language] || descriptions.es;

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: titles[language] || titles.es },
      { property: 'og:description', content: descriptions[language] || descriptions.es },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://liberado.app/ai-powered' },
      { property: 'og:image', content: 'https://liberado.app/og-image.png' }
    ];

    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.content = tag.content;
    });
  }, [language]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
      <SharedHeader />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 mb-8 transition">
            ← {language === 'es' ? 'Volver al inicio' : language === 'en' ? 'Back to home' : 'Voltar ao início'}
          </Link>

          {/* Title */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {language === 'es' ? 'IA & Innovación' : language === 'en' ? 'AI & Innovation' : 'IA & Inovação'}
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              {language === 'es' 
                ? 'Tecnología de vanguardia diseñada para transformar el mercado laboral veterinario en LATAM'
                : language === 'en'
                ? 'Cutting-edge technology designed to transform the veterinary labor market in LATAM'
                : 'Tecnologia de ponta projetada para transformar o mercado de trabalho veterinário na LATAM'
              }
            </p>
          </div>

          {/* AI Features */}
          <div className="space-y-8">
            {/* Feature 1 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">🔍</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Módulo de Verificación de Credenciales Veterinarias con IA (Futuro)' : language === 'en' ? 'AI Vet Credential Verification Module (Future)' : 'Módulo de Verificação de Credenciais Veterinárias com IA (Futuro)'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed mb-4">
                    {language === 'es'
                      ? 'Sistema automatizado futuro de validación de certificaciones profesionales, licencias y credenciales en tiempo real. Utilizará reconocimiento óptico de caracteres (OCR), verificación cruzada con bases de datos oficiales y detección de fraudes mediante machine learning. Esto reducirá drásticamente los riesgos de contratación y establecerá nuevos estándares de confianza en el sector.'
                      : language === 'en'
                      ? 'Future automated system for validating professional certifications, licenses, and credentials in real-time. Will use optical character recognition (OCR), cross-verification with official databases, and fraud detection through machine learning. This will drastically reduce hiring risks and establish new trust standards in the sector.'
                      : 'Futuro sistema automatizado de validação de certificações profissionais, licenças e credenciais em tempo real. Utilizará reconhecimento óptico de caracteres (OCR), verificação cruzada com bancos de dados oficiais e detecção de fraudes através de machine learning. Isso reduzirá drasticamente os riscos de contratação e estabelecerá novos padrões de confiança no setor.'
                    }
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">OCR</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">Machine Learning</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Detección de Fraudes' : language === 'en' ? 'Fraud Detection' : 'Detecção de Fraudes'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">💬</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Análisis de Experiencia y Habilidades con NLP (Futuro)' : language === 'en' ? 'NLP Experience & Skill Parsing (Future)' : 'Análise de Experiência e Habilidades com NLP (Futuro)'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed mb-4">
                    {language === 'es'
                      ? 'Motor futuro de procesamiento de lenguaje natural que interpretará descripciones de trabajos, perfiles profesionales y requisitos de clínicas. Extraerá automáticamente habilidades clave, especialidades y experiencias relevantes de textos no estructurados, permitiendo emparejamientos más precisos y reduciendo sesgos en procesos de selección.'
                      : language === 'en'
                      ? 'Future natural language processing engine that will interpret job descriptions, professional profiles, and clinic requirements. Will automatically extract key skills, specialties, and relevant experiences from unstructured text, enabling more accurate matches and reducing bias in selection processes.'
                      : 'Futuro mecanismo de processamento de linguagem natural que interpretará descrições de trabalho, perfis profissionais e requisitos de clínicas. Extrairá automaticamente habilidades-chave, especialidades e experiências relevantes de textos não estruturados, permitindo correspondências mais precisas e reduzindo vieses em processos de seleção.'
                    }
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">NLP</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Análisis de Texto' : language === 'en' ? 'Text Analysis' : 'Análise de Texto'}</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Extracción de Entidades' : language === 'en' ? 'Entity Extraction' : 'Extração de Entidades'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">🎯</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Motor de Emparejamiento Semántico y Ranking (Futuro)' : language === 'en' ? 'Semantic Matching & Ranking Engine (Future)' : 'Motor de Correspondência Semântica e Classificação (Futuro)'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed mb-4">
                    {language === 'es'
                      ? 'Algoritmos futuros avanzados que irán más allá del simple emparejamiento de palabras clave, comprendiendo el contexto y significado semántico profundo de habilidades y experiencias. Clasificará candidatos según compatibilidad multidimensional: especialidades, ubicación, disponibilidad, historial y preferencias mutuas. Esto optimizará resultados y creará valor sostenible.'
                      : language === 'en'
                      ? 'Future advanced algorithms that will go beyond simple keyword matching, understanding the deep semantic context and meaning of skills and experiences. Will rank candidates based on multidimensional compatibility: specialties, location, availability, history, and mutual preferences. This will optimize outcomes and create sustainable value.'
                      : 'Futuros algoritmos avançados que irão além da simples correspondência de palavras-chave, compreendendo o contexto semântico profundo e significado de habilidades e experiências. Classificará candidatos com base em compatibilidade multidimensional: especialidades, localização, disponibilidade, histórico e preferências mútuas. Isso otimizará resultados e criará valor sustentável.'
                    }
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Análisis Semántico' : language === 'en' ? 'Semantic Analysis' : 'Análise Semântica'}</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Ranking Multidimensional' : language === 'en' ? 'Multidimensional Ranking' : 'Classificação Multidimensional'}</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">ML</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">📅</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'IA de Programación Inteligente + Prevención de Conflictos (Futuro)' : language === 'en' ? 'Smart Scheduling AI + Conflict Prevention (Future)' : 'IA de Agendamento Inteligente + Prevenção de Conflitos (Futuro)'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed mb-4">
                    {language === 'es'
                      ? 'Sistema futuro que optimizará horarios considerando múltiples variables: disponibilidad, requisitos operativos, regulaciones laborales, distancias y patrones históricos. Predecirá y prevendrá conflictos antes de que ocurran, sugiriendo horarios óptimos que maximicen eficiencia, satisfacción y reducción de costos operativos para todas las partes.'
                      : language === 'en'
                      ? 'Future system that will optimize schedules considering multiple variables: availability, operational requirements, labor regulations, distances, and historical patterns. Will predict and prevent conflicts before they occur, suggesting optimal schedules that maximize efficiency, satisfaction, and operational cost reduction for all parties.'
                      : 'Futuro sistema que otimizará horários considerando múltiplas variáveis: disponibilidade, requisitos operacionais, regulamentações trabalhistas, distâncias e padrões históricos. Preverá e prevenirá conflitos antes que ocorram, sugerindo horários ótimos que maximizem eficiência, satisfação e redução de custos operacionais para todas as partes.'
                    }
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Optimización' : language === 'en' ? 'Optimization' : 'Otimização'}</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Predicción' : language === 'en' ? 'Prediction' : 'Previsão'}</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Prevención de Conflictos' : language === 'en' ? 'Conflict Prevention' : 'Prevenção de Conflitos'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">🌐</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Adaptación Multilingüe con IA (ES/EN/PT)' : language === 'en' ? 'Multilingual AI Adaptation (ES/EN/PT)' : 'Adaptação Multilíngue com IA (ES/EN/PT)'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed mb-4">
                    {language === 'es'
                      ? 'Capa de traducción neural y adaptación contextual en tiempo real que soporta español, inglés y portugués. Comprende jerga veterinaria y terminología técnica específica de cada región. Esto facilitará la expansión regional de LIBERADO en toda Latinoamérica, manteniendo significado profesional y contexto cultural en todas las comunicaciones.'
                      : language === 'en'
                      ? 'Neural translation and real-time contextual adaptation layer supporting Spanish, English, and Portuguese. Understands veterinary jargon and region-specific technical terminology. This will facilitate LIBERADO\'s regional expansion across Latin America, maintaining professional meaning and cultural context in all communications.'
                      : 'Camada de tradução neural e adaptação contextual em tempo real que suporta espanhol, inglês e português. Compreende jargão veterinário e terminologia técnica específica de cada região. Isso facilitará a expansão regional da LIBERADO em toda a América Latina, mantendo significado profissional e contexto cultural em todas as comunicações.'
                    }
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Traducción Neural' : language === 'en' ? 'Neural Translation' : 'Tradução Neural'}</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">ES/EN/PT</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Contexto Regional' : language === 'en' ? 'Regional Context' : 'Contexto Regional'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 6 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">🤝</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Agente de Soporte para Clínicas y Técnicos (Futuro)' : language === 'en' ? 'AI Clinic + Technician Support Agent (Future)' : 'Agente de Suporte para Clínicas e Técnicos (Futuro)'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed mb-4">
                    {language === 'es'
                      ? 'Asistente virtual futuro disponible 24/7 que responderá preguntas, guiará usuarios a través de procesos complejos, resolverá problemas comunes y escalará casos críticos al equipo humano cuando sea necesario. Aprenderá continuamente de cada interacción, mejorando respuestas y reduciendo costos operativos mientras mantiene alta calidad de servicio.'
                      : language === 'en'
                      ? 'Future virtual assistant available 24/7 that will answer questions, guide users through complex processes, resolve common issues, and escalate critical cases to the human team when necessary. Will continuously learn from each interaction, improving responses and reducing operational costs while maintaining high service quality.'
                      : 'Futuro assistente virtual disponível 24/7 que responderá perguntas, orientará usuários através de processos complexos, resolverá problemas comuns e encaminhará casos críticos para a equipe humana quando necessário. Aprenderá continuamente de cada interação, melhorando respostas e reduzindo custos operacionais enquanto mantém alta qualidade de serviço.'
                    }
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">24/7</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">Chatbot</span>
                    <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 rounded-full text-sm text-yellow-200">{language === 'es' ? 'Aprendizaje Continuo' : language === 'en' ? 'Continuous Learning' : 'Aprendizagem Contínua'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AI Disclaimer */}
          <div className="mt-12 bg-yellow-500 bg-opacity-10 border border-yellow-500 rounded-lg p-6">
            <p className="text-sm text-yellow-200">
              {language === 'es'
                ? '⚠️ Algunas funciones de inteligencia artificial aún están en desarrollo y se implementarán progresivamente durante el piloto en Chile.'
                : language === 'en'
                ? '⚠️ Some artificial intelligence features are still in development and will be implemented progressively during the Chile pilot.'
                : '⚠️ Algumas funcionalidades de inteligência artificial ainda estão em desenvolvimento e serão implementadas progressivamente durante o piloto no Chile.'
              }
            </p>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center bg-yellow-500 bg-opacity-10 border-2 border-yellow-500 rounded-lg p-8">
            <h2 className="text-3xl font-bold mb-4">
              {language === 'es' ? 'Experimente el futuro de la industria veterinaria' : language === 'en' ? 'Experience the future of the veterinary industry' : 'Experimente o futuro da indústria veterinária'}
            </h2>
            <p className="text-lg text-gray-200 mb-6">
              {language === 'es'
                ? 'Únase al piloto de Chile y sea parte de la innovación tecnológica del sector'
                : language === 'en'
                ? 'Join the Chile pilot and be part of the sector\'s technological innovation'
                : 'Junte-se ao piloto do Chile e faça parte da inovação tecnológica do setor'
              }
            </p>
            <Link to="/interes">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-black text-lg px-8 py-4 font-bold shadow-xl">
                {t('registerForInvitation')} →
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
};

export default AIPowered;
